import React, { useState } from 'react';
import {
    X, Download, Trash2, RefreshCw, File,
    Calendar, HardDrive, FileText, Tag, Copy, Check
} from 'lucide-react';
import { deleteMedia, redownloadMedia, forceDeleteMedia } from '../../services/mediaApi';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../ui/tooltip';

const API_BASE_URL = window._env_ ? window._env_.API_URL : process.env.REACT_APP_API_URL;

const MediaDetail = ({ media, onClose, onMediaUpdated }) => {
    const [copied, setCopied] = useState(false);

    if (!media) return null;

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Helper function to get complete media URL
    const getFullMediaUrl = (path) => {
        if (!path) return null;
        // If path already has http or https, return as is
        if (path.startsWith('http://') || path.startsWith('https://')) {
            return path;
        }
        // Otherwise, join with API_BASE_URL
        return `${API_BASE_URL}${path}`;
    };

    const handleCopyUrl = () => {
        if (media.storageInfo?.publicUrl) {
            const fullUrl = getFullMediaUrl(media.storageInfo.publicUrl);

            // Try to use the Clipboard API with fallback to execCommand
            const copyToClipboard = (text) => {
                // Try using the Clipboard API first
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    return navigator.clipboard.writeText(text)
                        .then(() => true)
                        .catch(() => false);
                }

                // Fallback to execCommand
                try {
                    const textarea = document.createElement('textarea');
                    textarea.value = text;
                    textarea.style.position = 'fixed';  // Prevent scrolling to bottom
                    document.body.appendChild(textarea);
                    textarea.focus();
                    textarea.select();

                    const successful = document.execCommand('copy');
                    document.body.removeChild(textarea);
                    return Promise.resolve(successful);
                } catch (err) {
                    return Promise.resolve(false);
                }
            };

            copyToClipboard(fullUrl)
                .then((success) => {
                    if (success) {
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                    } else {
                        alert('Failed to copy URL. Please select and copy manually.');
                    }
                });
        }
    };

    const handleDelete = async () => {
        const confirmMessage = media.status === 'deleted'
            ? 'This will permanently remove the file record from the database. Are you sure?'
            : 'Are you sure you want to delete this media file?';

        if (window.confirm(confirmMessage)) {
            try {
                let response;

                if (media.status === 'deleted') {
                    // If already deleted, use force delete to remove from database
                    response = await forceDeleteMedia(media._id);
                } else {
                    // Otherwise use regular delete (soft delete)
                    response = await deleteMedia(media._id);
                }

                if (response.status === 'success') {
                    onClose();
                    onMediaUpdated();
                } else {
                    throw new Error(response.message || 'Failed to delete media file');
                }
            } catch (error) {
                console.error('Error deleting media:', error);
                alert('Failed to delete media file');
            }
        }
    };

    const handleRedownload = async () => {
        try {
            const response = await redownloadMedia(media._id, media.mediaId);
            if (response.status === 'success') {
                onMediaUpdated();
            } else {
                throw new Error(response.message || 'Failed to redownload media file');
            }
        } catch (error) {
            console.error('Error redownloading media:', error);
            alert('Failed to redownload media file');
        }
    };

    const renderMediaPreview = () => {
        if (media.type === 'image' && media.storageInfo?.publicUrl) {
            return (
                <img
                    src={getFullMediaUrl(media.storageInfo.publicUrl)}
                    alt={media.originalName}
                    className="max-w-full max-h-[400px] object-contain rounded-lg"
                />
            );
        } else if (media.type === 'video' && media.storageInfo?.publicUrl) {
            return (
                <video
                    src={getFullMediaUrl(media.storageInfo.publicUrl)}
                    controls
                    className="max-w-full max-h-[400px] rounded-lg"
                />
            );
        } else if (media.type === 'audio' && media.storageInfo?.publicUrl) {
            return (
                <audio
                    src={getFullMediaUrl(media.storageInfo.publicUrl)}
                    controls
                    className="w-full rounded-lg"
                />
            );
        } else {
            return (
                <div className="flex flex-col items-center justify-center bg-gray-50 p-10 rounded-lg">
                    <File className="h-20 w-20 text-gray-400" />
                    <span className="mt-4 text-gray-600">{media.type} file</span>
                </div>
            );
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-xl font-semibold text-gray-800 truncate">
                        {media.originalName}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-1 rounded-full hover:bg-gray-100"
                    >
                        <X className="h-6 w-6 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex flex-col md:flex-row overflow-auto">
                    {/* Media Preview */}
                    <div className="p-6 flex items-center justify-center border-b md:border-b-0 md:border-r flex-shrink-0 md:w-1/2">
                        {renderMediaPreview()}
                    </div>

                    {/* Media Info */}
                    <div className="p-6 md:w-1/2 overflow-y-auto">
                        <h3 className="text-lg font-medium text-gray-800 mb-4">Media Information</h3>

                        <div className="space-y-4">
                            <div className="flex items-start gap-3">
                                <Tag className="h-5 w-5 text-gray-400 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium text-gray-500">Type</p>
                                    <p className="text-gray-800 capitalize">{media.type} ({media.mimeType})</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <HardDrive className="h-5 w-5 text-gray-400 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium text-gray-500">Size</p>
                                    <p className="text-gray-800">{formatFileSize(media.size)}</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium text-gray-500">Created At</p>
                                    <p className="text-gray-800">{new Date(media.createdAt).toLocaleString()}</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium text-gray-500">Filename</p>
                                    <p className="text-gray-800 break-all">{media.filename}</p>
                                </div>
                            </div>

                            {media.mediaId && (
                                <div className="flex items-start gap-3">
                                    <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Media ID</p>
                                        <p className="text-gray-800">{media.mediaId}</p>
                                    </div>
                                </div>
                            )}

                            {media.messageId && (
                                <div className="flex items-start gap-3">
                                    <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Message ID</p>
                                        <p className="text-gray-800 break-all">{media.messageId}</p>
                                    </div>
                                </div>
                            )}

                            <div className="flex items-start gap-3">
                                <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium text-gray-500">Status</p>
                                    <p className={`capitalize font-medium ${media.status === 'uploaded' ? 'text-green-600' :
                                        media.status === 'failed' ? 'text-red-600' :
                                            media.status === 'deleted' ? 'text-gray-600' : 'text-yellow-600'
                                        }`}>{media.status}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer with actions */}
                <div className="p-4 border-t bg-gray-50 flex items-center justify-end space-x-3">
                    {(media.status === 'failed' || media.status === 'deleted') && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={handleRedownload}
                                        className="flex items-center justify-center bg-indigo-600 text-white p-2 rounded-lg hover:bg-indigo-700"
                                    >
                                        <RefreshCw className="h-5 w-5" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                    <p>Redownload</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                    {media.storageInfo?.publicUrl && (
                        <>
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button
                                            onClick={handleCopyUrl}
                                            className="flex items-center justify-center bg-green-600 text-white p-2 rounded-lg hover:bg-green-700"
                                        >
                                            {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                        <p>{copied ? 'Copied!' : 'Copy URL'}</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>

                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <a
                                            href={getFullMediaUrl(media.storageInfo.publicUrl)}
                                            download={media.originalName}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center justify-center bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700"
                                        >
                                            <Download className="h-5 w-5" />
                                        </a>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                        <p>Download</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </>
                    )}
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    onClick={handleDelete}
                                    className={`flex items-center justify-center ${media.status === 'deleted'
                                            ? 'bg-red-700 hover:bg-red-800'
                                            : 'bg-red-600 hover:bg-red-700'
                                        } text-white p-2 rounded-lg`}
                                >
                                    <Trash2 className={`${media.status === 'deleted' ? 'h-5 w-5 animate-pulse' : 'h-5 w-5'
                                        }`} />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                                <p>{media.status === 'deleted' ? 'Permanently Delete' : 'Delete'}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            </div>
        </div>
    );
};

export default MediaDetail; 